import {
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  TemplateRef
} from '@angular/core';
import {StringIDdisplay} from '../../../types';

export interface JBMAutocompleteGetDataEvent {
  search: string;
  filterFields: string[];
  filters: { [key: string]: any };
}

export interface JBMAutocompleteSelectEvent<T = any> {
  value: any;
  data: T;
}

@Component({
  selector: 'jbm-autocomplete',
  templateUrl: './JBMAutocomplete.component.html',
  styleUrls: ['./JBMAutocomplete.component.scss']
})
export class JBMAutocompleteComponent implements OnChanges, OnDestroy {
  readonly stringIDdisplayType = StringIDdisplay;

  @Input() id: string='autocomplete';
  @Input('class') class: string='';
  @Input() labelCaption: string='';
  @Input() labelClass: string='';
  @Input() inputClass: string='';
  @Input() labelStacked: boolean=false;
  @Input() editable: boolean=true;
  @Input() required: boolean=false;
  @Input() submitted: boolean=false;
  @Input() sm: boolean=true;
  @Input() tabIndex: number | null=null;
  @Input() options: any[]=[];
  @Input() filters: { [key: string]: any }={};
  @Input() valueKey: string='id';
  @Input() displayKey: string='name';
  @Input() selectedDisplayKey: string='';
  @Input() stringIDdisplay: StringIDdisplay=StringIDdisplay.none;
  @Input() placeholder: string='';
  @Input() maxlength: number=0;
  @Input() minLength: number=0;
  @Input() debounce: number=250;
  @Input() loading: boolean=false;
  @Input() disabled: boolean=false;
  @Input() loadOnFocus: boolean=true;
  @Input() className: string='';

  @Input()
  set filterFields(value: string[] | string) {
    if(Array.isArray(value)) {
      this._filterFields=value;
      return;
    }
    this._filterFields=(value || '').split(',').map((field: string) => field.trim()).filter((field: string) => field!=='');
  }
  get filterFields(): string[] {
    return this._filterFields;
  }

  @Input()
  set value(value: any) {
    this.setOptionKey(value);
  }
  get value(): any {
    return this._value;
  }

  @Input()
  set optionKey(value: any) {
    this.setOptionKey(value);
  }
  get optionKey(): any {
    return this._value;
  }

  @Output() onGetData = new EventEmitter<JBMAutocompleteGetDataEvent>();
  @Output() onSelect = new EventEmitter<JBMAutocompleteSelectEvent>();
  @Output() onChange = new EventEmitter<string>();
  @Output() onBlur = new EventEmitter<void>();
  @Output() optionKeyChange = new EventEmitter<any>();

  @ContentChild(TemplateRef) optionTemplate: TemplateRef<any>;

  searchValue: string='';
  listOpen: boolean=false;
  activeOptionIndex: number=-1;

  private _filterFields: string[]=[];
  private _value: any=null;
  private getDataTimer: any;

  constructor(private eRef: ElementRef) {}

  ngOnChanges(changes: SimpleChanges): void {
    if(changes.hasOwnProperty('options')) {
      if(this.options===undefined) this.options=[];
      this.setActiveOptionIndex();
      this.updateDisplayFromValue();
    }
  }

  focus() {
    if(this.disabled) return;
    this.openList();
    if(this.loadOnFocus && this.getMatchedOptions().length===0)
      this.queueGetData();
  }

  input(event: any) {
    if(this.disabled) return;
    this.searchValue=(event.target as HTMLInputElement).value;
    this.clearOptionKey();
    this.onChange.emit(this.searchValue);
    this.openList();
    if(this.getMatchedOptions().length===0)
      this.queueGetData();
  }

  keyDown(event: KeyboardEvent) {
    if(this.disabled) return;
    const matchedOptions = this.getMatchedOptions();

    if(event.key==='ArrowDown') {
      event.preventDefault();
      if(!this.listOpen) {
        this.openList();
        return;
      }
      if(matchedOptions.length)
        this.activeOptionIndex=this.activeOptionIndex>=matchedOptions.length-1 ? 0 : this.activeOptionIndex+1;
    }

    if(event.key==='ArrowUp') {
      event.preventDefault();
      if(!this.listOpen) {
        this.openList();
        return;
      }
      if(matchedOptions.length)
        this.activeOptionIndex=this.activeOptionIndex<=0 ? matchedOptions.length-1 : this.activeOptionIndex-1;
    }

    if(event.key==='Enter' && this.listOpen && this.activeOptionIndex>-1) {
      event.preventDefault();
      this.selectOption(matchedOptions[this.activeOptionIndex]);
    }

    if(event.key==='Escape')
      this.hideList();
  }

  blur() {
    setTimeout(() => this.hideList(), 150);
    this.onBlur.emit();
  }

  selectLookupOption(option: any, event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.selectOption(option);
  }

  selectOption(option: any) {
    this._value = this.getOptionValue(option);
    this.searchValue=this.getSelectedDisplayValue(option);
    this.hideList();
    this.optionKeyChange.emit(this._value);
    this.onSelect.emit(option);
  }

  getDisplayValue(option: any): string {
    if(option===undefined || option===null) return '';
    const label=this.getOptionLabel(option);
    const id=this.getOptionValue(option);

    if(id===null || id===undefined || id==='')
      return label;

    if(this.stringIDdisplay===StringIDdisplay.displayIdBefore)
      return `#${id} ${label}`;

    if(this.stringIDdisplay===StringIDdisplay.displayIdAfter)
      return `${label} (#${id})`;

    return label;
  }

  getSelectedDisplayValue(option: any): string {
    if(this.selectedDisplayKey && option && option[this.selectedDisplayKey]!==undefined && option[this.selectedDisplayKey]!==null)
      return option[this.selectedDisplayKey].toString();
    return this.getDisplayValue(option);
  }

  getOptionLabel(option: any): string {
    if(option===undefined || option===null) return '';
    if(this.displayKey && option[this.displayKey]!==undefined && option[this.displayKey]!==null)
      return option[this.displayKey].toString();
    return option.toString();
  }

  getOptionValue(option: any): any {
    if(option===undefined || option===null) return null;
    if(this.valueKey && option[this.valueKey]!==undefined)
      return option[this.valueKey];
    return option;
  }

  getMatchedOptions(): any[] {
    const search=this.normalizeSearchValue(this.searchValue);
    const options = this.options || [];
    if(search==='') return options;

    return options.filter((option: any) => {
      const values=this.getOptionFilterValues(option);
      return values.some((value: string) => this.normalizeSearchValue(value).indexOf(search)>=0);
    });
  }

  getOptionFilterValues(option: any): string[] {
    if(option===undefined || option===null) return [];
    const fields=this.filterFields.length ? this.filterFields : [this.displayKey];

    return fields
      .map((field: string) => option[field])
      .filter((value: any) => value!==undefined && value!==null)
      .map((value: any) => value.toString());
  }

  normalizeSearchValue(value: any): string {
    if(value===undefined || value===null) return '';
    return value.toString().trim().toLocaleLowerCase();
  }

  getDataValue(): string {
    if(this.searchValue) return this.searchValue;
    if(this._value===null || this._value===undefined) return '';
    return this._value.toString();
  }

  openList() {
    this.listOpen=true;
    this.setActiveOptionIndex();
  }

  hideList() {
    this.listOpen=false;
    this.activeOptionIndex=-1;
  }

  private queueGetData() {
    if(this.getDataTimer!==undefined)
      clearTimeout(this.getDataTimer);

    if(this.searchValue.length<this.minLength)
      return;

    this.getDataTimer=setTimeout(() => {
      this.onGetData.emit({
        search: this.searchValue,
        filterFields: this.filterFields,
        filters: this.filters || {}
      });
    }, this.debounce);
  }

  private setActiveOptionIndex() {
    this.activeOptionIndex=this.getMatchedOptions().length>0 ? 0 : -1;
  }

  private updateDisplayFromValue() {
    if(this._value===null || this._value===undefined) return;
    const option=(this.options || []).find((o: any) => this.getOptionValue(o)==this._value);
    if(option)
      this.searchValue=this.getSelectedDisplayValue(option);
  }

  private setOptionKey(value: any) {
    this._value=value;
    this.updateDisplayFromValue();
  }

  private clearOptionKey() {
    if(this._value===null || this._value===undefined) return;
    this._value=null;
    this.optionKeyChange.emit(this._value);
  }

  @HostListener('document:click', ['$event'])
  clickedOut(event: any) {
    if(!this.eRef.nativeElement.contains(event.target))
      this.hideList();
  }

  ngOnDestroy(): void {
    if(this.getDataTimer!==undefined)
      clearTimeout(this.getDataTimer);
  }
}
